import { useEffect, useMemo, useState } from 'react'
import { compact, sortBy } from 'lodash'
import { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import moment, { Moment } from 'moment'

import {
    useReceptionStatusManagerAdminBaseQuery,
    useReceptionStatusManagerOverviewQuery,
    useReceptionStatusBaseQuery,
    useReceptionStatusProvidersQuery,
    useReceptionStatusProviderDelayTimesQuery,
    useReceptionStatusManagerAutoSwipeDelayQuery,
} from '@docpace/reception-status-react-apollo'

import {
    ReceptionStatusProviderFragment,
    AdminDetailFragment,
    PracticeOverviewFragment,
    ManagerOverviewFragment,
} from '@docpace/shared-graphql/fragments'

export type DelaysOutput = {
    providerId: string
    providerMinutesLate: number
}

export type ProviderDelay = {
    providerId: string
    displayNameCompiled: string
    providerMinutesLate: number | string
}


export type PatientDelay = {
    appointmentId: string
    providerId: string
    displayNameCompiled: string
    minutesUntilIntake: number
}


interface UseReceptionStatusBaseProps {}


export const useReceptionStatusBase = (inputProps: UseReceptionStatusBaseProps = {}) => {
    const [hasNetworkError, setHasNetworkError] = useState<boolean>(false)
    const [isInitialized, setIsInitialized] = useState<boolean>(false)
    const [isTableDataInitialized, setIsTableDataInitialized] =
        useState<boolean>(false)
    const [fetchError, setFetchError] = useState<ErrorLike>()
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

    const {
        data: baseData,
        loading: baseIsLoading,
        networkStatus: baseNetworkStatus,
    } = useReceptionStatusBaseQuery({})

    const { practiceId, adminId, managerId } = baseData ?? {}

    const { data: { admin } = {}, loading: adminIsLoading } =
    useReceptionStatusManagerAdminBaseQuery({
        variables: { adminId: adminId ?? '' },
        skip: !adminId,
    })

    const { data: { manager } = {}, loading: managerIsLoading } =
        useReceptionStatusManagerOverviewQuery({
            variables: { managerId: managerId ?? '' },
            skip: !managerId,
        })

    const {
        data,
        loading: practiceIsLoading,
        networkStatus: practiceNetworkStatus,
    } = useReceptionStatusProvidersQuery({
        variables: {
            practiceId: practiceId ?? '',
        },
        skip: !practiceId,
    })

    const practice = data?.practice
    const providers = compact(sortBy(data?.providers?.nodes, 'displayNameCompiled'))

    const {
        refetch,
        loading: isLoadingDelays,
        data: receptionStatusProviderDelaysData,
        error: receptionStatusProviderDelaysError,
    }= useReceptionStatusProviderDelayTimesQuery(
        {
            variables: {
                practiceId: practiceId ?? '',
            },
            skip: !practiceId || !!fetchError,
        }
    )

    const { data: { actors } = {}, loading: swipeDelayLoading, error: swipeDelayError } =
        useReceptionStatusManagerAutoSwipeDelayQuery({
            variables: {
                typeBasedActorId: adminId ?? managerId ?? '',
            },
            skip: !adminId || !!managerId,
        })

    useEffect(() => {
        const error = receptionStatusProviderDelaysError ?? swipeDelayError
        if (error) setFetchError(error)
    }, [receptionStatusProviderDelaysError, swipeDelayError])

    const isLoading =
        baseIsLoading ||
        practiceIsLoading ||
        isLoadingDelays ||
        swipeDelayLoading ||
        adminIsLoading ||
        managerIsLoading

    useEffect(() => {
        if (baseNetworkStatus > 3 && practiceNetworkStatus > 3 && !baseData) {
            setHasNetworkError(true)
        } else if (
            baseNetworkStatus > 3 &&
            practiceNetworkStatus > 3 &&
            !practice
        ) {
            setHasNetworkError(true)
        } else if (baseNetworkStatus > 3 && practiceNetworkStatus > 3) {
            setHasNetworkError(false)
            setIsInitialized(true)
        } else {
            setHasNetworkError(false)
        }
    }, [
        practiceId,
        !!baseData,
        baseNetworkStatus,
        practiceNetworkStatus,
        isLoading,
    ])

    const delayNodes = receptionStatusProviderDelaysData?.providerDelays?.nodes
    const providerDelayTableData: ProviderDelay[] = useMemo(() => {
        if (providers?.length > -1 && delayNodes) {
            const merged = compact(delayNodes)?.map(({ providerId, ...providerDelay }) => {
                const provider = providers?.find(
                    (p) => {
                        return p?.providerId === providerId
                    }
                )
                return {
                    ...providerDelay,
                    ...provider,
                    providerId: provider?.providerId ?? '',
                    displayNameCompiled: provider?.displayNameCompiled ?? '',
                    providerMinutesLate: providerDelay?.['providerMinutesLate'],
                }
            })
            return merged
        } else {
            return []
        }
    }, [providers, delayNodes])

    useEffect(() => {
        setIsTableDataInitialized(true)
    }, [baseIsLoading, isInitialized])

    return {
        practice: practice ?? null,
        practiceId: practice?.practiceId ?? null,
        providers,
        providerIds: providers?.map(p=>p?.providerId), 
        providerDelayTableData,
        manager,
        managerId: managerId ?? null,
        admin,
        adminId: adminId ?? null,
        isLoading,
        hasNetworkError,
        isInitialized,
        isTableDataInitialized,
        lastUpdated,
        autoSwipeDelayTime:
            actors?.nodes?.[0]?.managerStatusSiteAutoSwipeDelaySeconds ?? 7,
        refetch: async () => {
            await refetch()
            setFetchError(undefined)
            setLastUpdated(new Date())
        },
        clearFetchError: () => setFetchError(undefined),
        fetchError,
    }
}
